import React, { useMemo, ReactNode, useContext, useEffect } from 'react'
import { setUser } from '@sentry/react'
import {
  SimpleUserGroupFragment,
  useMeQuery,
  UserRole,
} from '../../generated/graphql'
import { useAuthentication } from '../authentication/AuthenticationProvider'

export type ProfileContextValue = {
  isFetched: boolean
  id?: string
  displayName?: string
  groups?: SimpleUserGroupFragment[]
  isAdmin: boolean
}

export const ProfileContext = React.createContext<ProfileContextValue>({
  isFetched: false,
  isAdmin: false,
})

type Props = {
  children: ReactNode
}

export function ProfileProvider({ children }: Props): React.ReactElement {
  const { isLoggedIn } = useAuthentication()
  const { data } = useMeQuery({ skip: !isLoggedIn })

  const profile = data?.me

  useEffect(() => {
    setUser({ id: profile?.id })
  }, [profile])

  const profileContext = useMemo(
    (): ProfileContextValue => ({
      isFetched: profile !== undefined,
      id: profile?.id,
      displayName: profile?.displayName,
      groups: profile?.groups,
      isAdmin: profile?.role === UserRole.Admin,
    }),
    [profile],
  )

  return (
    <ProfileContext.Provider value={profileContext}>
      {children}
    </ProfileContext.Provider>
  )
}

export const useProfile = (): ProfileContextValue => useContext(ProfileContext)
